import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, RefreshCw, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ErrorLogs() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('');

  useEffect(() => {
    loadLogs();
  }, [filtroTipo]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const url = filtroTipo 
        ? `${BACKEND_URL}/api/admin/error-logs?tipo=${filtroTipo}&limit=200`
        : `${BACKEND_URL}/api/admin/error-logs?limit=200`;
        
      const response = await fetch(url, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs);
      } else {
        toast.error('Error al cargar logs');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const getTipoBadgeColor = (tipo) => {
    switch(tipo) {
      case 'formulario1_error':
      case 'formulario2_error':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'agendar_cita_error':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'cita_duplicada':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'formulario1_conexion_error':
      case 'formulario2_conexion_error':
      case 'agendar_cita_conexion_error':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const formatFecha = (fecha) => {
    try {
      return new Date(fecha).toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return fecha;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Header */}
      <header className="bg-gray-900/80 backdrop-blur-xl border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <img src="/kompa-logo.png" alt="Kompa" className="h-10 w-auto" />
          </div>
          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Error Logs
          </Badge>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Registro de Errores</h1>
            <p className="text-gray-400">Monitoreo de errores del sistema</p>
          </div>
          <Button
            onClick={loadLogs}
            className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Filtros */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Filtros</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={filtroTipo === '' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroTipo('')}
                className={filtroTipo === '' ? 'bg-yellow-500 text-black' : 'text-gray-400'}
              >
                Todos ({logs.length})
              </Button>
              <Button
                variant={filtroTipo === 'formulario1_error' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroTipo('formulario1_error')}
                className={filtroTipo === 'formulario1_error' ? 'bg-yellow-500 text-black' : 'text-gray-400'}
              >
                Formulario 1
              </Button>
              <Button
                variant={filtroTipo === 'formulario2_error' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroTipo('formulario2_error')}
                className={filtroTipo === 'formulario2_error' ? 'bg-yellow-500 text-black' : 'text-gray-400'}
              >
                Formulario 2
              </Button>
              <Button
                variant={filtroTipo === 'agendar_cita_error' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroTipo('agendar_cita_error')}
                className={filtroTipo === 'agendar_cita_error' ? 'bg-yellow-500 text-black' : 'text-gray-400'}
              >
                Agendamiento
              </Button>
              <Button
                variant={filtroTipo === 'cita_duplicada' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroTipo('cita_duplicada')}
                className={filtroTipo === 'cita_duplicada' ? 'bg-yellow-500 text-black' : 'text-gray-400'}
              >
                Citas Duplicadas
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Lista de Logs */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Cargando logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="text-center py-12">
              <p className="text-gray-400">No hay logs para mostrar</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {logs.map((log, index) => (
              <Card key={index} className="bg-gray-800/50 border-gray-700 hover:border-gray-600 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge className={getTipoBadgeColor(log.tipo)}>
                          {log.tipo}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {formatFecha(log.fecha)}
                        </span>
                      </div>
                      
                      {log.usuario && (
                        <div className="text-sm text-gray-300 mb-2">
                          <span className="font-semibold">Usuario:</span>{' '}
                          {log.usuario.nombre || log.usuario.email || 'N/A'}
                          {log.usuario.cedula && ` (${log.usuario.cedula})`}
                        </div>
                      )}
                      
                      <div className="text-sm text-red-400 mb-2">
                        <span className="font-semibold">Error:</span> {log.error_tecnico || log.mensaje}
                      </div>
                      
                      {log.endpoint && (
                        <div className="text-xs text-gray-500">
                          <span className="font-semibold">Endpoint:</span> {log.endpoint}
                        </div>
                      )}
                      
                      {log.expediente_id && (
                        <div className="text-xs text-gray-500">
                          <span className="font-semibold">Expediente:</span> {log.expediente_id}
                        </div>
                      )}
                      
                      {log.fecha_intento && (
                        <div className="text-xs text-gray-500">
                          <span className="font-semibold">Fecha Intento:</span> {formatFecha(log.fecha_intento)}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
